import { apiClient } from '../../../../api/axios';
import type { ApiResponse, PaginatedResponse, Submission, DeskItem } from '../common/types';
import type {
    JournalAccessorDropdownItem,
    AssignMembersForJournalAssessmentPayload,
    AssignJournalAssessmentPanelResult,
    GetJournalsByDeskPayload,
    GetMemberAssignedSubmissionsForJournalAssessmentPayload,
    JournalAssessmentAssignedSubmission,
    JournalAssignmentDetail,
    CreateUpdateJournalAssessmentPayload,
    JournalAssessmentProcessItem,
    GetJournalsByAssessmentStatusPayload,
    SubmitJournalFinalDecisionPayload,
    JournalAssessmentFinalDecisionDetail,
} from './types';

export const journalsByDeskService = {
    getJournalsByDesk: async (
        payload: GetJournalsByDeskPayload
    ): Promise<ApiResponse<PaginatedResponse<Submission>>> => {
        const response = await apiClient.post('/Submissions/GetJournalByDesk', {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            sortBy: payload.sortBy || '',
            sortDescending: payload.sortDescending ?? true,
            search: payload.search || '',
            categoryId: 1,
        });
        return response.data;
    },

    getJournalDeskList: async (): Promise<ApiResponse<DeskItem[]>> => {
        const response = await apiClient.get('/Roles/GetJournalDeskList');
        return response.data;
    },

    getJournalAccessors: async (): Promise<ApiResponse<JournalAccessorDropdownItem[]>> => {
        const response = await apiClient.get('/ReviewCategory/GetJournalAccessorsDropdown');
        return response.data;
    },

    assignMembersForJournalAssessment: async (
        payload: AssignMembersForJournalAssessmentPayload
    ): Promise<ApiResponse<AssignJournalAssessmentPanelResult[]>> => {
        const response = await apiClient.post(
            '/Journals/GroupMemberAssignmentForJournalAssessment',
            payload
        );
        return response.data;
    },

    getMemberAssignedSubmissionsForJournalAssessment: async (
        payload: GetMemberAssignedSubmissionsForJournalAssessmentPayload
    ): Promise<ApiResponse<PaginatedResponse<JournalAssessmentAssignedSubmission>>> => {
        const response = await apiClient.post(
            '/Journals/GetMemberAssignedSubmissionsForJournalAssessment',
            {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                sortBy: payload.sortBy || '',
                sortDescending: payload.sortDescending ?? true,
            }
        );
        return response.data;
    },
};

export const journalAssessmentService = {
    getJournalAssignment: async (
        journalAssignmentId: string
    ): Promise<ApiResponse<JournalAssignmentDetail>> => {
        const response = await apiClient.get(
            `/Journals/GetJournalAssignment?journalAssignmentId=${journalAssignmentId}`
        );
        return response.data;
    },

    createUpdateJournalAssessment: async (
        payload: CreateUpdateJournalAssessmentPayload
    ): Promise<ApiResponse<string>> => {
        const response = await apiClient.post(
            '/Journals/CreateUpdateJournalAssessment',
            payload
        );
        return response.data;
    },
};

export const journalAssessmentReportService = {
    getJournalsByAssessmentStatus: async (
        payload: GetJournalsByAssessmentStatusPayload
    ): Promise<ApiResponse<PaginatedResponse<JournalAssessmentProcessItem>>> => {
        const response = await apiClient.post('/Journals/GetJournalsByAssessmentStatus', {
            pageNumber: payload.pageNumber,
            pageSize: payload.pageSize,
            sortBy: payload.sortBy || '',
            sortDescending: payload.sortDescending ?? true,
            search: payload.search || '',
            completed: payload.completed,
        });
        return response.data;
    },

    submitFinalDecision: async (
        payload: SubmitJournalFinalDecisionPayload
    ): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post('/Journals/SubmitFinalDecision', payload);
        return response.data;
    },

    getFinalDecisionBySubmissionId: async (
        submissionId: string
    ): Promise<ApiResponse<JournalAssessmentFinalDecisionDetail>> => {
        const response = await apiClient.get(
            `/Journals/GetJournalAssessmentFinalDecisionBySubmissionId?SubmissionId=${submissionId}`
        );
        return response.data;
    },

    deleteJournalAssignment: async (
        journalAssignmentId: string
    ): Promise<ApiResponse<boolean>> => {
        const response = await apiClient.post(
            `/Journals/DeleteJournalAssignment?journalAssignmentId=${journalAssignmentId}`
        );
        return response.data;
    },
};
